import { useState } from "react";
import { Star } from "lucide-react";
import JsonLd from "@/components/site/JsonLd";

const SITE_URL = process.env.REACT_APP_SITE_URL || "";

const reviews = [
  {
    name: "Clint",
    date: "Jul 2026",
    iso: "2026-06-01",
    dateUnverified: true,
    text: "Chris is a really nice guy, it's worth the trip to Villa Rica. Thanks.",
  },
  {
    name: "Heidi",
    date: "May 2026",
    iso: "2026-05-07",
    text: "About to make so many orders lol — these are awesome & I love my bird bath so much!",
  },
  {
    name: "John",
    date: "Feb 2026",
    iso: "2026-02-03",
    text: "I would buy again. Honest and prices are fair. He had more items to choose from.",
  },
  {
    name: "Derek",
    date: "Apr 2025",
    iso: "2025-04-25",
    text: "Garden Fountains is great. Great product, great guy. Thanks!",
  },
  {
    name: "Judith",
    date: "Mar 2025",
    iso: "2025-03-24",
    text: "Super nice!!! Excellent communication! Delivers on time and I looove my angels that I purchased!",
  },
  {
    name: "Deborah",
    date: "Mar 2025",
    iso: "2025-03-08",
    text: "Excellent communication!",
  },
  {
    name: "Nathan",
    date: "Feb 2025",
    iso: "2025-02-11",
    text: "Very good communication.",
  },
  {
    name: "Cecily",
    date: "Jul 2024",
    iso: "2024-07-29",
    text: "Amazing — has held on to my order for over 6 months now and remained true to his word… very thankful.",
  },
  {
    name: "Scott",
    date: "Jul 2024",
    iso: "2024-07-13",
    text: "Cool stuff… thanks!",
  },
  {
    name: "Demetra",
    date: "Feb 2024",
    iso: "2024-02-19",
    text: "The birdbath was exactly as described. Chris was very helpful and upfront. He made a new one just for me!",
  },
  {
    name: "Derek",
    date: "Sep 2022",
    iso: "2022-09-12",
    text: "Solid, easy to work with, knowledgeable and all around great guy! Definitely will use again!",
  },
];

const sortedReviews = [...reviews].sort((a, b) => b.iso.localeCompare(a.iso));
const displayReviews = sortedReviews.slice(0, 6);

const REVIEWS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#business`,
  name: "Garden Fountains Yard Sculptures",
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5",
    bestRating: "5",
    reviewCount: reviews.length,
  },
  review: reviews.map((r) => ({
    "@type": "Review",
    author: { "@type": "Person", name: r.name },
    ...(r.dateUnverified ? {} : { datePublished: r.iso }),
    reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
    reviewBody: r.text,
  })),
};

const Stars = () => (
  <div className="flex gap-0.5 mb-4" role="img" aria-label="5 out of 5 stars">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={14} strokeWidth={1} className="text-[#C8B387]" fill="currentColor" aria-hidden="true" focusable="false" />
    ))}
  </div>
);

export default function Reviews() {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? sortedReviews : displayReviews;
  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="bg-[#F9F6F0] py-10 md:py-14 border-t border-[#D5CEBD]"
    >
      <JsonLd id="reviews-schema" data={REVIEWS_SCHEMA} />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-3xl">
            <div className="section-eyebrow mb-5">
              <span>Reviews</span>
            </div>
            <h2
              data-testid="reviews-heading"
              className="font-serif font-light text-[#2C2A28] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6"
            >
              What our customers say.
            </h2>
            <p className="text-[#5C5955] text-base md:text-lg leading-relaxed max-w-2xl">
              Real reviews from real customers, collected on Facebook Marketplace.
            </p>
          </div>
          <div className="flex items-center gap-3 self-start md:self-end">
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={18} strokeWidth={1} className="text-[#C8B387]" fill="currentColor" />
              ))}
            </div>
            <span className="text-[#5C5955] text-sm whitespace-nowrap">5.0 · {reviews.length} reviews</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {visible.map((r, i) => (
            <figure
              key={`${r.name}-${r.iso}`}
              data-testid={`review-card-${i}`}
              className="bg-[#EFEBE1] border border-[#D5CEBD] p-6 md:p-7 flex flex-col"
            >
              <Stars />
              <blockquote className="font-serif text-lg md:text-xl text-[#2C2A28] font-light leading-relaxed italic flex-1">
                &ldquo;{r.text}&rdquo;
              </blockquote>
              <figcaption className="mt-5 flex items-baseline justify-between gap-3">
                <span className="text-[11px] tracking-[0.24em] uppercase text-[#705C40]">{r.name}</span>
                <span className="text-[#66625A] text-xs">{r.date}</span>
              </figcaption>
            </figure>
          ))}
        </div>
        <div className="mt-10 flex justify-center text-center">
          <button
            type="button"
            onClick={() => setShowAll((s) => !s)}
            data-testid="reviews-toggle-all"
            className="inline-flex items-center gap-2 text-[#7A6547] hover:text-[#5E4D35] text-sm tracking-[0.22em] uppercase border-b border-[#7A6547]/40 hover:border-[#7A6547] pb-1 transition-colors"
          >
            {showAll
              ? "Show fewer reviews"
              : `Read all ${reviews.length} five-star reviews →`}
          </button>
        </div>
      </div>
    </section>
  );
}
